import { GetErpInboundsReq } from '@/services/inbound/erpInbound/types'

/**
 * 메뉴 세션·URL 쿼리에 싣는 조회 조건 모양. (MOMS 공통 UX — 다른 메뉴를 다녀와도 마지막 조회 상태가 남는다)
 *
 * 조회 조건과 목록 검색어를 싣는다. 페이지는 싣지 않는다 — 전량을 받아 화면이 나누므로 페이지 이동이 조회 조건이 아니다(LOGISPRD-15268).
 *
 * 저장 대상은 조회영역의 네 필드 전부다 — 생성 뷰(`handoff/page.generated`)의 조회영역은 값이 상태(`filterInitial`)로
 * 들어가는 제어 컴포넌트라 어느 필드든 되돌려 심을 수 있다. 입고번호가 빠지면 화주만으로 전체 조회가 돈다(LOGISPRD-14198).
 *
 * 입고번호는 `Utils.qsStringify`(comma·encode:false) → `useParsedQuery`(comma) 왕복이라 하나씩 인코딩해 싣고,
 * 1건이면 문자열로 돌아오므로 배열로 맞춘다(rgr01V2 `searchQuery.ts` 와 같은 방식).
 */
export interface ErpInboundSearchQuery {
  shipperCode?: string
  orderType?: string
  transportType?: string
  /** 입고번호(여러 건). 1건이면 URL 에서 문자열로 돌아온다. */
  ordOptionNumbers?: string[] | string
  /** 표 위 목록 검색어. 받은 행을 거르는 클라이언트 필터다. */
  keyword?: string
}

/** 화면 상태 → 저장 형태. 빈 값은 키 자체를 빼 쿼리스트링을 짧게 유지한다. */
export const toSearchQuery = (state: { params: GetErpInboundsReq; keyword: string }): ErpInboundSearchQuery => ({
  ...(state.params.shipperCode ? { shipperCode: state.params.shipperCode } : {}),
  ...(state.params.ordOptionNumbers?.length ? { ordOptionNumbers: state.params.ordOptionNumbers.map(encodeURIComponent) } : {}),
  ...(state.params.orderType ? { orderType: state.params.orderType } : {}),
  ...(state.params.transportType ? { transportType: state.params.transportType } : {}),
  ...(state.keyword ? { keyword: state.keyword } : {}),
})

/** 문자열이 아닌 값(배열 등 조작된 URL)은 무시한다 — 화면이 깨지지 않게 한다. */
const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined)
const list = (value: unknown) => (Array.isArray(value) ? value : [value]).filter((v): v is string => typeof v === 'string' && !!v)

export interface RestoredErpInboundState {
  params: GetErpInboundsReq
  keyword: string
  /** 복원할 조회 조건이 실제로 있었는지. 있을 때만 마운트 시점에 조회를 돌린다. */
  hasQuery: boolean
}

/**
 * 저장된 쿼리를 화면 상태로 되돌린다.
 *
 * 화주명이 비면 세션 화주(`defaultShipperCode`)로 채운다 — 필터의 화주명이 필수 필드라 빈 값으로
 * 조회하면 서버가 거절한다.
 *
 * `hasQuery` 는 URL 에 조회 조건이 하나라도 있었는지로 판정한다. 최초 진입(빈 URL)에서는 false 라
 * 지금처럼 빈 표로 열리고, 탭을 다녀온 경우에만 조회가 자동으로 돈다.
 */
export const fromSearchQuery = (query: Partial<ErpInboundSearchQuery>, defaultShipperCode: string): RestoredErpInboundState => {
  const shipperCode = text(query.shipperCode)
  const ordOptionNumbers = list(query.ordOptionNumbers)
  const orderType = text(query.orderType)
  const transportType = text(query.transportType)

  return {
    params: {
      ...(shipperCode || defaultShipperCode ? { shipperCode: shipperCode ?? defaultShipperCode } : {}),
      ...(ordOptionNumbers.length ? { ordOptionNumbers } : {}),
      ...(orderType ? { orderType } : {}),
      ...(transportType ? { transportType } : {}),
    },
    keyword: text(query.keyword) ?? '',
    hasQuery: !!shipperCode,
  }
}
